import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { DebugTabs } from '@/components/debug/DebugTabs';
import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/session';
import {
  useMnaAdapterHealth,
  useMnaQuartz,
  useMnaRejectStats,
  useMnaReviewAction,
  useMnaReviewQueue,
  useMnaRunJob,
  useMnaUsage,
} from '../lib/api';
import type { AdapterHealth, QuartzJobDto, RejectReasonCode, ReviewItemDto } from '../types';
import { SourceLink } from './_shared';

// M&A Debug — admin-only (MnaApp adds the tab only for portal admins; in A6 the BFF proxy
// attaches the trusted header so MNA.Api's /api/admin gate passes). Four sections in the
// WWD Debug idiom: the human review queue, the Quartz schedule (manual triggers), the
// per-adapter sources health (Block 2), and the provider usage rollup.

type TabId = 'review' | 'quartz' | 'sources' | 'usage';

export default function Debug() {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;
  const [tab, setTab] = useState<TabId>('review');

  const review = useMnaReviewQueue(isAdmin);
  const quartz = useMnaQuartz(isAdmin);
  const adapters = useMnaAdapterHealth(isAdmin);

  if (!isAdmin) {
    return (
      <Card>
        <CardTitle>Debug</CardTitle>
        <CardDescription className="mt-2">The M&amp;A debug console is admin-only.</CardDescription>
      </Card>
    );
  }

  const tabs = [
    { id: 'review', label: 'Review Queue', count: review.data?.length ?? 0 },
    { id: 'quartz', label: 'Quartz Schedule', count: quartz.data?.length ?? 0 },
    { id: 'sources', label: 'Sources Health', count: adapters.data?.length ?? 0 },
    { id: 'usage', label: 'Usage' },
  ];

  return (
    <>
      <Card>
        <CardTitle>Debug</CardTitle>
        <CardDescription className="mt-2">
          Human review queue, Quartz schedule with manual triggers, and provider usage.{' '}
          <span className="text-accent">Admin-only</span>
        </CardDescription>
      </Card>

      <DebugTabs tabs={tabs} value={tab} onChange={(id) => setTab(id as TabId)} />

      {tab === 'review' && (
        review.isLoading ? <LoadingSkeleton lines={6} /> :
        review.error ? <ErrorState message={(review.error as Error).message} /> :
        <ReviewQueue items={review.data ?? []} />
      )}

      {tab === 'quartz' && (
        quartz.isLoading ? <LoadingSkeleton lines={6} /> :
        quartz.error ? <ErrorState message={(quartz.error as Error).message} /> :
        <QuartzSection jobs={quartz.data ?? []} />
      )}

      {tab === 'sources' && (
        adapters.isLoading ? <LoadingSkeleton lines={6} /> :
        adapters.error ? <ErrorState message={(adapters.error as Error).message} /> :
        <SourcesHealthSection rows={adapters.data ?? []} />
      )}

      {tab === 'usage' && <UsageSection />}
    </>
  );
}

/* ---------- Review queue ---------- */

// B4: reject reason-codes — chips shown on the second click of Reject. Labels stay short;
// the code strings are the server contract (RejectReasons.All).
const REJECT_REASONS: Array<{ code: RejectReasonCode; label: string }> = [
  { code: 'not_a_company', label: 'not a company' },
  { code: 'operator_noc', label: 'operator/NOC' },
  { code: 'out_of_sector', label: 'out of sector' },
  { code: 'out_of_size', label: 'out of size' },
  { code: 'duplicate', label: 'duplicate' },
  { code: 'job_title_noise', label: 'job-title noise' },
  { code: 'other', label: 'other' },
];

function ReviewQueue({ items }: { items: ReviewItemDto[] }) {
  const action = useMnaReviewAction();
  // A4 guardrail (the 2026-07-07 bulk-approve lesson): company items get a two-click
  // confirm showing the name — approving a COMPANY admits it to the scored universe, a
  // much bigger action than publishing one event. Events/facts keep one-click.
  const [confirming, setConfirming] = useState<string | null>(null);
  // B4: reject always goes through a reason picker (one extra click, reject only).
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [approvedThisSession, setApprovedThisSession] = useState(0);

  const act = (id: string, kind: ReviewItemDto['kind'], verb: 'approve' | 'reject') => {
    if (verb === 'reject' && rejecting !== id) {
      setRejecting(id);
      setConfirming(null);
      return;
    }
    if (kind === 'companies' && verb === 'approve' && confirming !== id) {
      setConfirming(id);
      setRejecting(null);
      return;
    }
    setConfirming(null);
    action.mutate(
      { id, action: verb },
      { onSuccess: () => verb === 'approve' && setApprovedThisSession((n) => n + 1) }
    );
  };

  const rejectWithReason = (id: string, reasonCode: RejectReasonCode) => {
    setRejecting(null);
    action.mutate({ id, action: 'reject', reasonCode });
  };

  return (
    <Card>
      <h2 className="text-xl font-semibold mb-3">
        Review Queue{' '}
        <span className="text-muted text-sm font-normal">
          (rows flagged needs_review — approve to publish, reject to discard)
        </span>
        {approvedThisSession > 0 && (
          <span className="ml-2 rounded-(--radius-sm) border border-accent px-2 py-0.5 text-xs font-normal text-accent">
            approved this session: {approvedThisSession}
          </span>
        )}
      </h2>
      {items.length === 0 ? (
        <p className="text-muted">Nothing awaiting review.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-accent">
                <Th>Table</Th>
                <Th>Company</Th>
                <Th>Summary</Th>
                <Th>Flagged</Th>
                <Th right>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border align-top hover:bg-surface-hover">
                  <td className="py-3 px-3 whitespace-nowrap text-muted">
                    {it.kind}
                    <div className="font-mono text-xs text-subtle">{it.id}</div>
                  </td>
                  <td className="py-3 px-3 whitespace-nowrap">
                    <Link to={`/mna/companies/${it.companySlug}`} className="text-accent hover:underline">
                      {it.companyName}
                    </Link>
                  </td>
                  <td className="py-3 px-3 max-w-96">
                    <span className="text-text">{it.summary}</span>{' '}
                    {it.source && <SourceLink source={it.source} />}
                    {it.detail && <p className="mt-1 text-xs text-muted">{it.detail}</p>}
                  </td>
                  <td className="py-3 px-3 whitespace-nowrap text-muted">{fmtDateTime(it.flaggedAt)}</td>
                  <td className="py-3 px-3 text-right whitespace-nowrap">
                    {confirming === it.id ? (
                      <>
                        <span className="mr-2 text-xs text-accent-danger">
                          Admit “{it.companyName}” to the universe?
                        </span>
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={action.isPending}
                          onClick={() => act(it.id, it.kind, 'approve')}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-2"
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : rejecting === it.id ? (
                      <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                        <span className="text-xs text-muted">Reject as:</span>
                        {REJECT_REASONS.map((r) => (
                          <Button
                            key={r.code}
                            size="sm"
                            variant="danger"
                            disabled={action.isPending}
                            onClick={() => rejectWithReason(it.id, r.code)}
                          >
                            {r.label}
                          </Button>
                        ))}
                        <Button size="sm" variant="ghost" onClick={() => setRejecting(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={action.isPending}
                          onClick={() => act(it.id, it.kind, 'approve')}
                        >
                          Approve{it.kind === 'companies' ? '…' : ''}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          className="ml-2"
                          disabled={action.isPending}
                          onClick={() => act(it.id, it.kind, 'reject')}
                        >
                          Reject…
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {action.error && <p className="mt-3 text-sm text-accent-danger">{(action.error as Error).message}</p>}
      <RejectStatsSection />
    </Card>
  );
}

/* B4 — reject reason mining: what the queue is teaching us about the screens. */
function RejectStatsSection() {
  const { data } = useMnaRejectStats(true);
  const reasons = data?.reasons ?? [];
  if (reasons.length === 0) return null;

  return (
    <div className="mt-6 border-t border-border pt-4">
      <h3 className="text-sm font-semibold uppercase tracking-widest text-muted font-(family-name:--font-ui)">
        Reject reasons (feeds screen tuning)
      </h3>
      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <Th>Reason</Th>
            <Th right>30d</Th>
            <Th right>90d</Th>
            <Th>Recent</Th>
          </tr>
        </thead>
        <tbody>
          {reasons.map((r) => (
            <tr key={r.reasonCode} className="border-b border-border align-top">
              <td className="py-2 px-3 font-mono text-xs">{r.reasonCode}</td>
              <td className="py-2 px-3 text-right tabular-nums">{r.count30d}</td>
              <td className="py-2 px-3 text-right tabular-nums">{r.count90d}</td>
              <td className="py-2 px-3 text-xs text-muted">
                {r.recent.slice(0, 8).map((i) => i.companyName).join(', ')}
                {r.recent.length > 8 && ` +${r.recent.length - 8} more`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Quartz schedule ---------- */

function QuartzSection({ jobs }: { jobs: QuartzJobDto[] }) {
  const run = useMnaRunJob();
  const [lastResult, setLastResult] = useState<string | null>(null);

  return (
    <Card>
      <h2 className="text-xl font-semibold mb-3">Quartz Schedule</h2>
      {jobs.length === 0 ? (
        <p className="text-muted">Worker unreachable or no jobs registered.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-accent">
              <Th>Job</Th>
              <Th>Cron</Th>
              <Th>Last fire</Th>
              <Th>Next fire</Th>
              <Th right>Run</Th>
            </tr>
          </thead>
          <tbody>
            {[...jobs].sort((a, b) => a.key.localeCompare(b.key)).map((j) => (
              <tr key={j.key} className="border-b border-border hover:bg-surface-hover">
                <td className="py-3 px-3 font-medium">
                  {j.key}
                  {j.description && <p className="mt-0.5 text-xs font-normal text-muted">{j.description}</p>}
                </td>
                <td className="py-3 px-3 text-muted font-mono text-xs">{j.cron ?? '—'}</td>
                <td className="py-3 px-3 text-muted">{fmtRelative(j.lastFireTimeUtc)}</td>
                <td className="py-3 px-3 text-muted">{fmtRelative(j.nextFireTimeUtc)}</td>
                <td className="py-3 px-3 text-right">
                  <Button
                    size="sm"
                    disabled={run.isPending}
                    onClick={() =>
                      run.mutate(j.key, {
                        onSuccess: (r) => setLastResult(r.message),
                        onError: (e) => setLastResult((e as Error).message),
                      })
                    }
                  >
                    ▶ Run
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {lastResult && <p className="mt-3 text-sm text-muted">{lastResult}</p>}
    </Card>
  );
}

/* ---------- Sources health (Block 2) ---------- */

const DAY_MS = 24 * 60 * 60 * 1000;

function SourcesHealthSection({ rows }: { rows: AdapterHealth[] }) {
  const now = Date.now();
  const runStale = (iso: string) => now - Date.parse(iso) > 8 * DAY_MS; // everything runs at least weekly
  const yieldStale = (iso: string | null) => iso === null || now - Date.parse(iso) > 14 * DAY_MS;

  return (
    <Card>
      <h2 className="text-xl font-semibold mb-3">Sources Health</h2>
      <p className="mb-3 text-sm text-muted">
        One row per signal adapter: when it last ran and when it last yielded anything (events, facts
        or review stubs). A quiet feed is a growing gap here, not log silence. Rows appear after an
        adapter&apos;s first post-upgrade run.
      </p>
      {rows.length === 0 ? (
        <p className="text-muted">No adapter runs recorded yet (or Worker unreachable).</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-accent">
              <Th>Adapter</Th>
              <Th>Last run</Th>
              <Th>Last yield</Th>
              <Th right>Items</Th>
              <Th right>Published</Th>
              <Th right>Stubs</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.adapter} className="border-b border-border hover:bg-surface-hover">
                <td className="py-3 px-3 font-medium font-mono text-xs">{r.adapter}</td>
                <td className={`py-3 px-3 ${runStale(r.lastRunAt) ? 'text-accent-danger font-medium' : 'text-muted'}`}>
                  {fmtRelative(r.lastRunAt)}
                  {runStale(r.lastRunAt) && ' ⚠'}
                </td>
                <td className={`py-3 px-3 ${yieldStale(r.lastYieldAt) ? 'text-accent-warn' : 'text-muted'}`}>
                  {r.lastYieldAt ? fmtRelative(r.lastYieldAt) : 'never'}
                </td>
                <td className="py-3 px-3 text-right text-muted [font-variant-numeric:tabular-nums]">{r.lastItemsSeen}</td>
                <td className="py-3 px-3 text-right text-muted [font-variant-numeric:tabular-nums]">{r.lastPublished}</td>
                <td className="py-3 px-3 text-right text-muted [font-variant-numeric:tabular-nums]">{r.lastStubs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ---------- Usage ---------- */

const selectClass =
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11 ' +
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent';

function UsageSection() {
  const [month, setMonth] = useState(currentMonth);
  const { data, isLoading, error } = useMnaUsage(month, true);
  const months = lastNMonths(12);

  const totalCost = data?.providers.reduce((s, p) => s + (p.costUsd ?? 0), 0) ?? 0;
  const totalCalls = data?.providers.reduce((s, p) => s + p.calls, 0) ?? 0;

  return (
    <>
      <Card>
        <h2 className="text-xl font-semibold">API Usage</h2>
        <p className="text-muted text-sm mt-1">Monthly provider spend and call volume.</p>
        <label className="flex items-center gap-2 mt-4">
          <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">Month</span>
          <select className={selectClass} value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      </Card>

      {isLoading && <LoadingSkeleton lines={4} />}
      {error && <ErrorState message={(error as Error).message} />}

      {data && (
        <Card>
          <div className="flex flex-wrap gap-x-8 gap-y-3 mb-4">
            <Stat label="Total cost" value={fmtUsd.format(totalCost)} />
            <Stat label="API calls" value={fmtNum.format(totalCalls)} />
            <Stat label="Providers" value={String(data.providers.length)} />
          </div>
          {data.providers.length === 0 ? (
            <p className="text-muted text-sm">No API usage recorded for {month}.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <Th>Provider</Th>
                  <Th right>Calls</Th>
                  <Th right>Cost (USD)</Th>
                  <Th right>Tokens in</Th>
                  <Th right>Tokens out</Th>
                </tr>
              </thead>
              <tbody>
                {data.providers.map((r) => (
                  <tr key={r.provider} className="border-b border-border hover:bg-surface-hover">
                    <td className="py-3 px-3 font-medium">{r.provider}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{fmtNum.format(r.calls)}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{r.costUsd != null ? fmtUsd.format(r.costUsd) : '—'}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{r.tokensIn != null ? fmtNum.format(r.tokensIn) : '—'}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{r.tokensOut != null ? fmtNum.format(r.tokensOut) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}

/* ---------- shared bits ---------- */

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui) ${right ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">{label}</div>
      <div className="text-2xl font-(family-name:--font-heading) font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

const fmtUsd = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const fmtNum = new Intl.NumberFormat();

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 16).replace('T', ' ');
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const delta = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(delta);
  const dir = delta < 0 ? 'ago' : 'in';
  const m = Math.round(abs / 60_000);
  if (m < 60) return `${m}m ${dir}`;
  const h = Math.round(abs / 3_600_000);
  if (h < 48) return `${h}h ${dir}`;
  const d = Math.round(abs / 86_400_000);
  return `${d}d ${dir}`;
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function lastNMonths(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
